import { Kaggle, LegacyDataSource } from "@kaggle/sdk";
import { getLogger, getServices } from "../../../api/getServices";
import { FailureType } from "../../types/FailureType";
import { ModalContentType } from "../../types/modal";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { ModalActionKey } from "./ActionKeys";

import AcceleratorType = Kaggle.Sdk.Kernels.AcceleratorType;
import ParentKernelVersionData = Kaggle.Sdk.Kernels.ParentKernelVersionData;
import UpdateDataSourceResult = Kaggle.Sdk.Kernels.UpdateDataSourceResult;

interface ShowModalAction
  extends AppAction<ModalActionKey.SHOW_MODAL, { content: ModalContentType }> {}
interface HideModalAction extends AppAction<ModalActionKey.HIDE_MODAL> {}

export interface ModalMetadata {
  accelerator?: AcceleratorType;
  acceleratorLabel?: string;
  executionTypeLabel?: string;
  computedParent?: ParentKernelVersionData;
  kernelId?: number;
  versionId?: number;
  runId?: number;
  kernelUrl?: string;
  code?: string;
  rulesAcceptance?: {
    competitionId: number;
    datasource: LegacyDataSource;
  };
  message?: string;
  updateDataSourceResult?: UpdateDataSourceResult;
  maxSessionCount?: number;
  saveDraftFailureError?: FailureType;
}
interface SetModalMetadataAction
  extends AppAction<ModalActionKey.SET_MODAL_METADATA, ModalMetadata> {}

interface SetCommitDemandModalShownAction
  extends AppAction<
    ModalActionKey.SET_COMMIT_DEMAND_MODAL_SHOWN,
    { shown: boolean }
  > {}

export type ModalAction =
  | ShowModalAction
  | HideModalAction
  | SetModalMetadataAction
  | SetCommitDemandModalShownAction;

const showModal = (content: ModalContentType): ShowModalAction =>
  getStore().dispatch({
    type: ModalActionKey.SHOW_MODAL,
    payload: { content },
  });

export const hideModal = (): HideModalAction =>
  getStore().dispatch({
    type: ModalActionKey.HIDE_MODAL,
    payload: {},
  });

export const showRulesAcceptanceModal = (
  competitionId: number,
  datasource: LegacyDataSource
) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { rulesAcceptance: { competitionId, datasource } },
  });
  showModal("rules_acceptance");
};

export const showCopyAndEditNudgeModal = (
  computedParent: ParentKernelVersionData
) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { computedParent },
  });
  showModal("copy_and_edit_nudge");
  getServices().analyticsClient.logger.report(
    "kernels",
    "copy-and-edit-nudge-modal-shown",
    {
      parentVersionId: computedParent.kernelVersionId,
    }
  );
};

export const showAcceleratorPromptModal = (accelerator: AcceleratorType) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { accelerator },
  });
  showModal("enable_accelerator_prompt");
};

export const showAcceleratorUnusedModal = (accelerator: AcceleratorType) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { accelerator },
  });
  showModal("accelerator_unused");
  getServices().analyticsClient.logger.report(
    "kernels",
    `accelerator-unused-shown`,
    {
      accelerator,
    }
  );
};

export const showSubmitModal = (runId?: number) => {
  getStore().dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { runId },
  });
  showModal("submit_to_competition");
};

export const showKernelSubmitModal = () => {
  showModal("kernel_submit");
};

export const showMaxSesionsExceededModal = (
  executionTypeLabel: string,
  acceleratorLabel: string,
  maxSessionCount: number
) => {
  getStore().dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { executionTypeLabel, acceleratorLabel, maxSessionCount },
  });
  getLogger().report("kernels", "max-sessions-exceeded-modal-shown");
  showModal("max_sessions_exceeded");
};

export const showOverFailureThresholdSizeModal = () => {
  getLogger().report("kernels", "over-failure-threshold-size-modal-shown");
  showModal("over_failure_threshold_size");
};

export const showSaveDraftFailureModal = (
  saveDraftFailureError: FailureType
) => {
  getStore().dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { saveDraftFailureError },
  });
  getLogger().report("kernels", "save-draft-failure-modal-shown", {
    saveDraftFailure: saveDraftFailureError,
  });
  showModal("save_draft_failure");
};

export const showAcceleratorQuotaExceededModal = (
  accelerator: AcceleratorType,
  acceleratorLabel: string
) => {
  getStore().dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { accelerator, acceleratorLabel },
  });
  getLogger().report("kernels", "accelerator-quota-exceeded-modal-shown");
  showModal("accelerator_quota_exceeded");
};

export const showAddDataModal = () => showModal("add_data");

export const showAddUtilityModal = () => showModal("add_utility");

export const showUploadDataModal = () => showModal("upload_data");

export const showCaipExportModal = () => showModal("caip_export");

export const showCommitDemandModal = () => {
  getStore().dispatch({
    type: ModalActionKey.SET_COMMIT_DEMAND_MODAL_SHOWN,
    payload: { shown: true },
  });
  showModal("commit_demand");
};

export const showGcloudAuthModal = () => showModal("gcloud_auth");

export const showGcpAuthModal = () => showModal("gcp_auth");

export const showUserSecretsModal = () => showModal("user_secrets");

export const showAboutModal = () => showModal("about");

export const showDockerModal = () => showModal("docker");

export const showSaveVersionModal = () => showModal("save_version");

export const showOpenNotebookModal = () => showModal("open_notebook");

export const showLinkToGitHubModal = () => showModal("link_to_github");

export const showSnippetEditorModal = () => showModal("snippet_editor");

export const showShareModal = () => showModal("share");

export const showFullScreenVersionModal = (
  versionId: number,
  runId: number
) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { versionId, runId },
  });
  dispatch(showModal("full_screen_version"));
};

export const showEditorIdleTimeOutModal = () =>
  showModal("editor_idle_time_out");

export const showUpdateDataSourceModal = (
  updateDataSourceResult: UpdateDataSourceResult
) => {
  const { dispatch } = getStore();
  dispatch({
    type: ModalActionKey.SET_MODAL_METADATA,
    payload: { updateDataSourceResult },
  });
  showModal("update_data_source");
};

export const showMoreRecentCollaboratorVersionModal = () =>
  showModal("more_recent_collaborator_version");

export const showLearnLoginPromptModal = () => showModal("learn_login_prompt");

export const showFatalErrorModal = () => showModal("fatal_error");

export const showForkSecretModal = () => {
  getLogger().report("kernels", "fork-secret-modal");
  showModal("fork_secret");
};

export const showScheduledNotebookIncompatibleGPUModal = () => {
  getLogger().report("kernels", "scheduled-notebook-incompatible-gpu-modal");
  showModal("scheduled_notebook_incompatible_gpu");
};

export const showScheduledNotebookLimitModal = () => {
  getLogger().report("kernels", "scheduled-notebook-limit-modal");
  showModal("scheduled_notebook_limit");
};
